import { useState, lazy, Suspense } from "react";
import { useNavigate } from "react-router";
import { EmptyState, Pagination } from "@/shared/components";
import { Prompt, PromptForm } from "@/shared/ui";
import { useAuth } from "@/shared/hooks";
import { PERMISSIONS } from "@/shared/consts";
import { ACCOUNTING_TIMESHEET_DETAIL_PAGE_ROUTE } from "@/shared/utils";
import { useTimesheetsListPage, useTimesheetModals, useTimesheetMutations, useTimesheetDownloads } from "../../hooks";
import { useLazyGetPreviousAllocationsQuery, useBulkUpdateWorkerAllocationsMutation } from "../../api";
import type { PreviousAllocationItem } from "../../types";
import { recomputeAllocations } from "../../utils";
import {
  TimesheetsHeader,
  TimesheetsPageSkeleton,
  TimesheetsFilter,
  DownloadTimesheetModal,
} from "./components";

const TimesheetsTable = lazy(() => import("./components/TimesheetsTable").then((m) => ({ default: m.default })));
const GenerateTimesheetPrompt = lazy(() => import("./components/GenerateTimesheetPrompt").then((m) => ({ default: m.default })));

export default function TimesheetsPage() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canGenerate = hasPermission(PERMISSIONS.HR.GENERATE_TIMESHEETS);

  const {
    data,
    isLoading,
    isError,
    filteredTimesheets,
    pagination,
    pageTimesheets,
    locale,
    t,
    sortConfig,
    handleSort,
    search,
    setSearch,
    dateRange,
    setDateRange,
    statusFilter,
    setStatusFilter,
    statusOptions,
    activeFilters,
    handleResetFilters,
  } = useTimesheetsListPage();

  const { generateModal, setGenerateModal, prompt, setPrompt } = useTimesheetModals();

  const { generating, isApproving, isDeleting, handleGenerate, handleApprove, handleDelete } =
    useTimesheetMutations(setPrompt);

  const { handleDownloadDocx, handleDownloadPdf, isDownloading } = useTimesheetDownloads(setPrompt);

  const [fetchPreviousAllocations] = useLazyGetPreviousAllocationsQuery();
  const [bulkUpdateAllocations] = useBulkUpdateWorkerAllocationsMutation();

  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const [confirmApprove, setConfirmApprove] = useState<number | null>(null);
  const [downloadTarget, setDownloadTarget] = useState<{ id: number; year: number; month: number } | null>(null);

  if (isLoading && !data) {
    return (
      <>
        <title>{t("meta.title")}</title>
        <section className="flex-1">
          <TimesheetsHeader />
          <TimesheetsPageSkeleton />
        </section>
      </>
    );
  }

  if (isError) {
    return (
      <>
        <title>{t("meta.title")}</title>
        <section className="flex-1">
          <div className="h-full component-scroll pr-5">
            <TimesheetsHeader />
            <p className="mt-4 text-body-regular-md content-action-negative">{t("messages.error")}</p>
          </div>
        </section>
      </>
    );
  }

  async function handleGenerateConfirm(dto: { year: number; month: number }) {
    const newTimesheet = await handleGenerate(dto);
    setGenerateModal(false);

    if (!newTimesheet?.id) return;

    try {
      const previous = await fetchPreviousAllocations(newTimesheet.id).unwrap();
      if (!previous.source_timesheet || previous.allocations.length === 0) return;

      const entries = newTimesheet.entries || [];
      const previousByWorker = new Map<number, PreviousAllocationItem[]>();
      for (const alloc of previous.allocations) {
        const existing = previousByWorker.get(alloc.worker_id) || [];
        existing.push(alloc);
        previousByWorker.set(alloc.worker_id, existing);
      }

      for (const entry of entries) {
        const prevAllocations = previousByWorker.get(entry.worker.id);
        if (!prevAllocations || prevAllocations.length === 0) continue;

        const recomputed = recomputeAllocations(prevAllocations, entry.work_hours);
        if (recomputed.length === 0) continue;

        await bulkUpdateAllocations({
          timesheetId: newTimesheet.id,
          workerId: entry.worker.id,
          body: { allocations: recomputed },
        }).unwrap();
      }
    } catch {
      // silently ignore — allocations are a best-effort auto-fill
    }
  }

  async function handleApproveConfirm(id: number) {
    await handleApprove(id);
    setConfirmApprove(null);
  }

  async function handleDeleteConfirm(id: number) {
    await handleDelete(id);
    setConfirmDelete(null);
  }

  return (
    <>
      <title>{t("meta.title")}</title>
      <section className="flex-1">
          <TimesheetsHeader />

          <div className="radius-3xl surface-base-fill p-7">
            <TimesheetsFilter
              search={search}
              onSearchChange={setSearch}
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
              statusFilter={statusFilter}
              onStatusChange={setStatusFilter}
              statusOptions={statusOptions}
              locale={locale}
              onGenerateClick={() => setGenerateModal(true)}
              canGenerate={canGenerate}
              hasActiveFilters={activeFilters}
              onReset={handleResetFilters}
            />

            {filteredTimesheets.length === 0 ? (
              <EmptyState
                hasActiveFilters={activeFilters}
                noResultsText={t("table.noResults")}
                noDataText={t("table.noData")}
                noDataHint={t("table.noDataHint")}
              />
            ) : (
              <>
                <Suspense fallback={<TimesheetsPageSkeleton variant="tableOnly" />}>
                  <TimesheetsTable
                    timesheets={pageTimesheets}
                    onOpen={(id) => navigate(ACCOUNTING_TIMESHEET_DETAIL_PAGE_ROUTE.replace(":id", String(id)))}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  />
                </Suspense>

                <Pagination
                  currentPage={pagination.page}
                  totalPages={pagination.totalPages}
                  onPageChange={pagination.setPage}
                  fromItem={pagination.fromItem}
                  toItem={pagination.toItem}
                  total={filteredTimesheets.length}
                />
              </>
            )}
          </div>
      </section>

      {generateModal && (
        <Suspense fallback={null}>
          <GenerateTimesheetPrompt
            isOpen={generateModal}
            onClose={() => setGenerateModal(false)}
            onConfirm={handleGenerateConfirm}
            isLoading={generating}
            timesheets={data}
          />
        </Suspense>
      )}

      {downloadTarget && (
        <DownloadTimesheetModal
          onClose={() => setDownloadTarget(null)}
          onDownloadDocx={() => handleDownloadDocx(downloadTarget.id, downloadTarget.year, downloadTarget.month)}
          onDownloadPdf={() => handleDownloadPdf(downloadTarget.id, downloadTarget.year, downloadTarget.month)}
        />
      )}

      {isDownloading && (
        <Prompt
          loaderMode={true}
          onClose={() => {}}
          title=""
          text=""
          loaderText={t("messages.loader.waitingForDownload.text")}
          additionalText={t("messages.loader.waitingForDownload.additionalText")}
          namespace="TimesheetsPage"
        />
      )}

      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant || "success"}
          onClose={() => setPrompt(null)}
          namespace="TimesheetsPage"
        />
      )}

      {confirmApprove !== null && (
        <PromptForm
          title={t("confirm.approveTitle")}
          text={t("confirm.approveText")}
          variant="warning"
          onClose={() => setConfirmApprove(null)}
          onConfirm={() => handleApproveConfirm(confirmApprove)}
          isLoading={isApproving}
          confirmText={t("confirm.approveConfirm")}
          cancelText={t("confirm.cancel")}
          namespace="TimesheetsPage"
        />
      )}

      {confirmDelete !== null && (
        <PromptForm
          title={t("confirm.deleteTitle")}
          text={t("confirm.deleteText")}
          variant="error"
          onClose={() => setConfirmDelete(null)}
          onConfirm={() => handleDeleteConfirm(confirmDelete)}
          isLoading={isDeleting}
          confirmText={t("confirm.deleteConfirm")}
          cancelText={t("confirm.cancel")}
          namespace="TimesheetsPage"
        />
      )}
    </>
  );
}
